import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * Manages the browser's push-notification subscription for the signed-in user.
 *
 * State:
 *   supported  — browser can do Web Push at all
 *   permission — 'default' | 'granted' | 'denied'
 *   subscribed — we currently hold a live subscription
 */
export function usePushNotifications() {
  const supported =
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window;

  const [permission, setPermission] = useState(supported ? Notification.permission : 'denied');
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!supported) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(!!sub);
    } catch (e) {
      setSubscribed(false);
    }
  }, [supported]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const enable = useCallback(async () => {
    if (!supported || busy) return { ok: false };
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') return { ok: false, reason: perm };

      const { data } = await axios.get(`${API_URL}/api/push/vapid-public-key`, {
        withCredentials: true,
      });
      const reg = await navigator.serviceWorker.ready;

      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.public_key),
        });
      }

      const json = sub.toJSON();
      await axios.post(
        `${API_URL}/api/push/subscribe`,
        { endpoint: sub.endpoint, keys: json.keys },
        { withCredentials: true }
      );
      setSubscribed(true);
      return { ok: true };
    } catch (e) {
      return { ok: false, reason: 'error' };
    } finally {
      setBusy(false);
    }
  }, [supported, busy]);

  const disable = useCallback(async () => {
    if (!supported || busy) return { ok: false };
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await axios.post(
          `${API_URL}/api/push/unsubscribe`,
          { endpoint: sub.endpoint, keys: sub.toJSON().keys },
          { withCredentials: true }
        ).catch(() => {});
        await sub.unsubscribe().catch(() => {});
      }
      setSubscribed(false);
      return { ok: true };
    } finally {
      setBusy(false);
    }
  }, [supported, busy]);

  return { supported, permission, subscribed, busy, enable, disable, refresh };
}
